import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import {
  EnvironmentId,
  EventId,
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas";

export const DEFAULT_BROWSER_ENVIRONMENT_ID = EnvironmentId.make("local");
export const DEFAULT_BROWSER_USER_ID = "local-user";

const BrowserUrl = TrimmedNonEmptyString;

export const BrowserId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserId = typeof BrowserId.Type;

export const BrowserSessionId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserSessionId = typeof BrowserSessionId.Type;

export const BrowserSessionEpoch = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserSessionEpoch = typeof BrowserSessionEpoch.Type;

export const BrowserFrameStreamId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserFrameStreamId = typeof BrowserFrameStreamId.Type;

export const BrowserTabId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserTabId = typeof BrowserTabId.Type;

export const BrowserCaptureId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserCaptureId = typeof BrowserCaptureId.Type;

export const BrowserUserId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserUserId = typeof BrowserUserId.Type;

export const BrowserAgentId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserAgentId = typeof BrowserAgentId.Type;

export const BrowserOwnerRole = Schema.Literals(["lead", "worker", "qa", "reviewer", "user"]);
export type BrowserOwnerRole = typeof BrowserOwnerRole.Type;

export const BrowserVisibility = Schema.Literals(["hidden", "visible"]);
export type BrowserVisibility = typeof BrowserVisibility.Type;

export const BrowserSessionCloseReason = Schema.Literals([
  "user_closed",
  "thread_archived",
  "idle_timeout",
  "clear_browsing_data",
  "runtime_shutdown",
]);
export type BrowserSessionCloseReason = typeof BrowserSessionCloseReason.Type;

export const BrowserRenderMode = Schema.Literals(["capture", "interactive"]);
export type BrowserRenderMode = typeof BrowserRenderMode.Type;

export const DEFAULT_BROWSER_RENDER_MODE: BrowserRenderMode = "interactive";

export function canonicalBrowserIdForThread(threadId: ThreadId): BrowserId {
  return BrowserId.make(`browser-${threadId}`);
}

function browserScopeHash(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/**
 * The canonical browser id for a thread in a browser scope. The default
 * environment and user keep the plain per-thread id; anything else hashes
 * the scope in via a 32-bit hash, so collisions are possible.
 */
export function canonicalBrowserIdForScope(input: {
  readonly threadId: ThreadId;
  readonly environmentId: EnvironmentId;
  readonly userId: BrowserUserId;
}): BrowserId {
  if (
    input.environmentId === DEFAULT_BROWSER_ENVIRONMENT_ID &&
    input.userId === DEFAULT_BROWSER_USER_ID
  ) {
    return canonicalBrowserIdForThread(input.threadId);
  }
  const suffix = String(input.threadId).slice(-64);
  return BrowserId.make(
    `browser-${browserScopeHash(`${input.environmentId}:${input.userId}:${input.threadId}`)}-${suffix}`,
  );
}

/**
 * Page-readiness of a tab's most recent requested navigation. Distinct from
 * BrowserTabStatus (tab liveness): a session is usable from `session.started`
 * (session-ready) while its initial navigation may still be `pending`.
 * Optional end-to-end: absent on older runtime bridges (pre-0.6.0 behavior) and
 * when no navigation was requested.
 */
export const BrowserNavigationStatus = Schema.Literals(["pending", "complete", "failed"]);
export type BrowserNavigationStatus = typeof BrowserNavigationStatus.Type;

export const BrowserTabStatus = Schema.Union([
  Schema.Literal("live"),
  Schema.Literal("restored"),
  Schema.Literal("degraded"),
]);
export type BrowserTabStatus = typeof BrowserTabStatus.Type;

export const BrowserViewport = Schema.Struct({
  width: Schema.Number,
  height: Schema.Number,
});
export type BrowserViewport = typeof BrowserViewport.Type;

export const BrowserDisplayTransport = Schema.Literals([
  "native-embedded",
  "native-extension",
  "remote-webrtc",
  "artifact-only",
  "unknown",
]);
export type BrowserDisplayTransport = typeof BrowserDisplayTransport.Type;

export const BrowserDisplayStatus = Schema.Literals([
  "connecting",
  "connected",
  "reconnecting",
  "degraded",
  "failed",
  "unsupported",
]);
export type BrowserDisplayStatus = typeof BrowserDisplayStatus.Type;

export const BrowserDisplayDegradedReason = Schema.Literals([
  "permission_denied",
  "transport_unavailable",
  "network_unreachable",
  "runtime_shutdown",
  "unsupported_host",
  "no_active_tab",
  "unknown",
]);
export type BrowserDisplayDegradedReason = typeof BrowserDisplayDegradedReason.Type;

export const BrowserDisplayReconnectState = Schema.Struct({
  status: Schema.Literals(["ready", "scheduled", "exhausted"]),
  attempt: Schema.Number.pipe(Schema.withDecodingDefault(Effect.succeed(0))),
  nextRetryAt: Schema.optional(IsoDateTime),
});
export type BrowserDisplayReconnectState = typeof BrowserDisplayReconnectState.Type;

export const BrowserDisplayIceServer = Schema.Struct({
  urls: Schema.Union([TrimmedNonEmptyString, Schema.Array(TrimmedNonEmptyString)]),
  username: Schema.optional(TrimmedNonEmptyString),
  credential: Schema.optional(Schema.String),
});
export type BrowserDisplayIceServer = typeof BrowserDisplayIceServer.Type;

export const BrowserDisplaySession = Schema.Struct({
  transport: BrowserDisplayTransport,
  status: BrowserDisplayStatus,
  reason: Schema.optional(BrowserDisplayDegradedReason),
  detail: Schema.optional(Schema.String),
  viewport: Schema.optional(BrowserViewport),
  devicePixelRatio: Schema.optional(Schema.Number),
  reconnect: Schema.optional(BrowserDisplayReconnectState),
  iceServers: Schema.optional(Schema.Array(BrowserDisplayIceServer)),
});
export type BrowserDisplaySession = typeof BrowserDisplaySession.Type;

export const BrowserDisplayClientId = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
export type BrowserDisplayClientId = typeof BrowserDisplayClientId.Type;

const BrowserDisplaySdp = Schema.String.check(Schema.isNonEmpty());

export const BrowserDisplayOffer = Schema.Struct({
  type: Schema.Literal("offer"),
  sdp: BrowserDisplaySdp,
});
export type BrowserDisplayOffer = typeof BrowserDisplayOffer.Type;

export const BrowserDisplayAnswer = Schema.Struct({
  type: Schema.Literal("answer"),
  sdp: BrowserDisplaySdp,
});
export type BrowserDisplayAnswer = typeof BrowserDisplayAnswer.Type;

export const BrowserDisplayIceCandidate = Schema.Struct({
  candidate: Schema.String,
  sdpMid: Schema.optional(Schema.NullOr(Schema.String)),
  sdpMLineIndex: Schema.optional(Schema.NullOr(Schema.Number)),
  usernameFragment: Schema.optional(Schema.NullOr(Schema.String)),
});
export type BrowserDisplayIceCandidate = typeof BrowserDisplayIceCandidate.Type;

/**
 * Which region of the runtime's X display a display peer receives.
 *
 * `browser` crops away Chromium's tab strip and omnibox so the viewer sees only
 * the page; `desktop` streams the whole screen including window-manager chrome
 * and any non-browser app. Both read the SAME display, so a session can serve
 * one of each simultaneously — which is why the capture mode is part of the
 * peer identity rather than a property of the runtime.
 */
export const BrowserDisplayCaptureMode = Schema.Literals(["browser", "desktop"]);
export type BrowserDisplayCaptureMode = typeof BrowserDisplayCaptureMode.Type;

export const BrowserDisplayAttachInput = Schema.Struct({
  threadId: ThreadId,
  /** Defaults to `browser` so existing clients keep the cropped page view. */
  captureMode: Schema.optional(BrowserDisplayCaptureMode),
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
  sessionId: BrowserSessionId,
  tabId: BrowserTabId,
  clientId: BrowserDisplayClientId,
  offer: BrowserDisplayOffer,
  viewport: Schema.optional(BrowserViewport),
  devicePixelRatio: Schema.optional(Schema.Number),
});
export type BrowserDisplayAttachInput = typeof BrowserDisplayAttachInput.Type;

export const BrowserDisplayAttachResult = Schema.Struct({
  sessionId: BrowserSessionId,
  tabId: BrowserTabId,
  clientId: BrowserDisplayClientId,
  display: BrowserDisplaySession,
  answer: BrowserDisplayAnswer,
});
export type BrowserDisplayAttachResult = typeof BrowserDisplayAttachResult.Type;

export const BrowserDisplayIceCandidateInput = Schema.Struct({
  threadId: ThreadId,
  /** Must match the attach, or the candidate routes to the wrong peer. */
  captureMode: Schema.optional(BrowserDisplayCaptureMode),
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
  sessionId: BrowserSessionId,
  tabId: BrowserTabId,
  clientId: BrowserDisplayClientId,
  candidate: Schema.NullOr(BrowserDisplayIceCandidate),
});
export type BrowserDisplayIceCandidateInput = typeof BrowserDisplayIceCandidateInput.Type;

export const BrowserDisplayMetrics = Schema.Struct({
  framesPerSecond: Schema.optional(Schema.Number),
  bitrateKbps: Schema.optional(Schema.Number),
  roundTripTimeMs: Schema.optional(Schema.Number),
  jitterMs: Schema.optional(Schema.Number),
  packetsLost: Schema.optional(Schema.Number),
  width: Schema.optional(Schema.Number),
  height: Schema.optional(Schema.Number),
});
export type BrowserDisplayMetrics = typeof BrowserDisplayMetrics.Type;

export const DEFAULT_BROWSER_DISPLAY_SESSION = {
  transport: "unknown",
  status: "unsupported",
  reason: "transport_unavailable",
  detail: "No browser display transport has attached.",
} satisfies BrowserDisplaySession;

export const BrowserConsoleLogLevel = Schema.Literals(["debug", "info", "warning", "error"]);
export type BrowserConsoleLogLevel = typeof BrowserConsoleLogLevel.Type;

export const BrowserConsoleLogEntry = Schema.Struct({
  level: BrowserConsoleLogLevel,
  message: Schema.String,
  createdAt: IsoDateTime,
});
export type BrowserConsoleLogEntry = typeof BrowserConsoleLogEntry.Type;

export const BrowserCommentPoint = Schema.Struct({
  x: Schema.Number,
  y: Schema.Number,
});
export type BrowserCommentPoint = typeof BrowserCommentPoint.Type;

export const BrowserCommentRect = Schema.Struct({
  x: Schema.Number,
  y: Schema.Number,
  width: Schema.Number,
  height: Schema.Number,
});
export type BrowserCommentRect = typeof BrowserCommentRect.Type;

export const BrowserCommentTarget = Schema.Struct({
  tabId: BrowserTabId,
  url: BrowserUrl,
  captureId: Schema.optional(BrowserCaptureId),
  capturedAt: Schema.optional(IsoDateTime),
  viewport: Schema.optional(BrowserViewport),
  point: Schema.optional(BrowserCommentPoint),
  rect: Schema.optional(BrowserCommentRect),
});
export type BrowserCommentTarget = typeof BrowserCommentTarget.Type;

export const BrowserCommentPayload = Schema.Struct({
  target: BrowserCommentTarget,
  note: Schema.optional(TrimmedNonEmptyString),
});
export type BrowserCommentPayload = typeof BrowserCommentPayload.Type;

export const BrowserTabSnapshot = Schema.Struct({
  tabId: BrowserTabId,
  restoreKey: TrimmedNonEmptyString,
  title: Schema.optional(TrimmedNonEmptyString),
  titleUpdatedAt: Schema.optional(IsoDateTime),
  url: BrowserUrl,
  isActive: Schema.Boolean,
  canGoBack: Schema.Boolean,
  canGoForward: Schema.Boolean,
  renderMode: BrowserRenderMode,
  status: BrowserTabStatus,
  lastCaptureId: Schema.optional(BrowserCaptureId),
  lastCapturedAt: Schema.optional(IsoDateTime),
  lastError: Schema.optional(TrimmedNonEmptyString),
  navigationStatus: Schema.optional(BrowserNavigationStatus),
  requestedUrl: Schema.optional(BrowserUrl),
  navigationError: Schema.optional(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});
export type BrowserTabSnapshot = typeof BrowserTabSnapshot.Type;

export const BrowserSessionSnapshot = Schema.Struct({
  browserId: BrowserId,
  threadId: ThreadId,
  environmentId: EnvironmentId.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_ENVIRONMENT_ID)),
  ),
  userId: BrowserUserId.pipe(Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_USER_ID))),
  agentId: Schema.NullOr(BrowserAgentId).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  ownerRole: BrowserOwnerRole.pipe(Schema.withDecodingDefault(Effect.succeed("lead" as const))),
  name: Schema.NullOr(TrimmedNonEmptyString).pipe(Schema.withDecodingDefault(Effect.succeed(null))),
  visibility: BrowserVisibility.pipe(Schema.withDecodingDefault(Effect.succeed("hidden" as const))),
  viewport: BrowserViewport.pipe(
    Schema.withDecodingDefault(Effect.succeed({ width: 1280, height: 720 })),
  ),
  display: BrowserDisplaySession.pipe(
    Schema.withDecodingDefault(Effect.succeed(DEFAULT_BROWSER_DISPLAY_SESSION)),
  ),
  sessionId: BrowserSessionId,
  sessionEpoch: Schema.optional(BrowserSessionEpoch),
  lastAppliedControlSequence: Schema.optional(NonNegativeInt),
  activeTabId: Schema.NullOr(BrowserTabId),
  tabs: Schema.Array(BrowserTabSnapshot),
  restoredAt: Schema.optional(IsoDateTime),
  updatedAt: IsoDateTime,
});
export type BrowserSessionSnapshot = typeof BrowserSessionSnapshot.Type;

const BrowserBaseTabAction = Schema.Struct({
  tabId: BrowserTabId,
});

export const BrowserNavigateCommand = Schema.Struct({
  type: Schema.Literal("navigate"),
  tabId: Schema.optional(BrowserTabId),
  url: BrowserUrl,
});
export type BrowserNavigateCommand = typeof BrowserNavigateCommand.Type;

export const BrowserNewTabCommand = Schema.Struct({
  type: Schema.Literal("newTab"),
  url: Schema.optional(BrowserUrl),
  makeActive: Schema.optional(Schema.Boolean),
});
export type BrowserNewTabCommand = typeof BrowserNewTabCommand.Type;

export const BrowserCloseTabCommand = Schema.Struct({
  type: Schema.Literal("closeTab"),
  ...BrowserBaseTabAction.fields,
});
export type BrowserCloseTabCommand = typeof BrowserCloseTabCommand.Type;

export const BrowserActivateTabCommand = Schema.Struct({
  type: Schema.Literal("activateTab"),
  ...BrowserBaseTabAction.fields,
});
export type BrowserActivateTabCommand = typeof BrowserActivateTabCommand.Type;

export const BrowserGoBackCommand = Schema.Struct({
  type: Schema.Literal("goBack"),
  ...BrowserBaseTabAction.fields,
});
export type BrowserGoBackCommand = typeof BrowserGoBackCommand.Type;

export const BrowserGoForwardCommand = Schema.Struct({
  type: Schema.Literal("goForward"),
  ...BrowserBaseTabAction.fields,
});
export type BrowserGoForwardCommand = typeof BrowserGoForwardCommand.Type;

export const BrowserReloadCommand = Schema.Struct({
  type: Schema.Literal("reload"),
  ...BrowserBaseTabAction.fields,
});
export type BrowserReloadCommand = typeof BrowserReloadCommand.Type;

export const BrowserClickCommand = Schema.Struct({
  type: Schema.Literal("click"),
  ...BrowserBaseTabAction.fields,
  selector: TrimmedNonEmptyString,
});
export type BrowserClickCommand = typeof BrowserClickCommand.Type;

export const BrowserTypeCommand = Schema.Struct({
  type: Schema.Literal("type"),
  ...BrowserBaseTabAction.fields,
  selector: TrimmedNonEmptyString,
  text: Schema.String,
  append: Schema.optional(Schema.Boolean),
});
export type BrowserTypeCommand = typeof BrowserTypeCommand.Type;

export const BrowserScrollCommand = Schema.Struct({
  type: Schema.Literal("scroll"),
  ...BrowserBaseTabAction.fields,
  x: Schema.optional(Schema.Number),
  y: Schema.optional(Schema.Number),
  toTop: Schema.optional(Schema.Boolean),
});
export type BrowserScrollCommand = typeof BrowserScrollCommand.Type;

export const BrowserPointerButton = Schema.Literals(["left", "middle", "right"]);
export type BrowserPointerButton = typeof BrowserPointerButton.Type;

export const BrowserClickPointCommand = Schema.Struct({
  type: Schema.Literal("clickPoint"),
  ...BrowserBaseTabAction.fields,
  x: Schema.Number,
  y: Schema.Number,
  button: Schema.optional(BrowserPointerButton),
  clickCount: Schema.optional(Schema.Number),
});
export type BrowserClickPointCommand = typeof BrowserClickPointCommand.Type;

export const BrowserWheelCommand = Schema.Struct({
  type: Schema.Literal("wheel"),
  ...BrowserBaseTabAction.fields,
  deltaX: Schema.Number,
  deltaY: Schema.Number,
  x: Schema.optional(Schema.Number),
  y: Schema.optional(Schema.Number),
});
export type BrowserWheelCommand = typeof BrowserWheelCommand.Type;

export const BrowserPressKeyCommand = Schema.Struct({
  type: Schema.Literal("pressKey"),
  ...BrowserBaseTabAction.fields,
  key: TrimmedNonEmptyString,
  x: Schema.optional(Schema.Number),
  y: Schema.optional(Schema.Number),
});
export type BrowserPressKeyCommand = typeof BrowserPressKeyCommand.Type;

export const BrowserTypeTextCommand = Schema.Struct({
  type: Schema.Literal("typeText"),
  ...BrowserBaseTabAction.fields,
  text: Schema.String,
  pressEnter: Schema.optional(Schema.Boolean),
});
export type BrowserTypeTextCommand = typeof BrowserTypeTextCommand.Type;

export const BrowserSnapshotCommand = Schema.Struct({
  type: Schema.Literal("snapshot"),
  ...BrowserBaseTabAction.fields,
});
export type BrowserSnapshotCommand = typeof BrowserSnapshotCommand.Type;

export const BrowserScreenshotCommand = Schema.Struct({
  type: Schema.Literal("screenshot"),
  ...BrowserBaseTabAction.fields,
  fullPage: Schema.optional(Schema.Boolean),
});
export type BrowserScreenshotCommand = typeof BrowserScreenshotCommand.Type;

export const BrowserSetViewportCommand = Schema.Struct({
  type: Schema.Literal("setViewport"),
  viewport: BrowserViewport,
});
export type BrowserSetViewportCommand = typeof BrowserSetViewportCommand.Type;

export const BrowserResetViewportCommand = Schema.Struct({
  type: Schema.Literal("resetViewport"),
});
export type BrowserResetViewportCommand = typeof BrowserResetViewportCommand.Type;

export const BrowserSetVisibilityCommand = Schema.Struct({
  type: Schema.Literal("setVisibility"),
  visibility: BrowserVisibility,
});
export type BrowserSetVisibilityCommand = typeof BrowserSetVisibilityCommand.Type;

export const BrowserNameSessionCommand = Schema.Struct({
  type: Schema.Literal("nameSession"),
  name: TrimmedNonEmptyString,
});
export type BrowserNameSessionCommand = typeof BrowserNameSessionCommand.Type;

export const BrowserConsoleLogsCommand = Schema.Struct({
  type: Schema.Literal("consoleLogs"),
  tabId: Schema.optional(BrowserTabId),
  limit: Schema.optional(Schema.Number),
  levels: Schema.optional(Schema.Array(BrowserConsoleLogLevel)),
});
export type BrowserConsoleLogsCommand = typeof BrowserConsoleLogsCommand.Type;

/**
 * Mute or unmute one tab's audio. A desktop-native concern: the real
 * webContents live in the desktop shell's browser view, so this command is
 * only issued over the desktop bridge, never through the server channel.
 */
export const BrowserSetAudioMutedCommand = Schema.Struct({
  type: Schema.Literal("setAudioMuted"),
  muted: Schema.Boolean,
});
export type BrowserSetAudioMutedCommand = typeof BrowserSetAudioMutedCommand.Type;

export const BrowserCommand = Schema.Union([
  BrowserNavigateCommand,
  BrowserNewTabCommand,
  BrowserCloseTabCommand,
  BrowserActivateTabCommand,
  BrowserGoBackCommand,
  BrowserGoForwardCommand,
  BrowserReloadCommand,
  BrowserClickCommand,
  BrowserTypeCommand,
  BrowserScrollCommand,
  BrowserClickPointCommand,
  BrowserWheelCommand,
  BrowserPressKeyCommand,
  BrowserTypeTextCommand,
  BrowserSnapshotCommand,
  BrowserScreenshotCommand,
  BrowserSetViewportCommand,
  BrowserResetViewportCommand,
  BrowserSetVisibilityCommand,
  BrowserSetAudioMutedCommand,
  BrowserNameSessionCommand,
  BrowserConsoleLogsCommand,
]);
export type BrowserCommand = typeof BrowserCommand.Type;

export const BrowserOpenSessionInput = Schema.Struct({
  threadId: ThreadId,
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
  agentId: Schema.optional(Schema.NullOr(BrowserAgentId)),
  ownerRole: Schema.optionalKey(BrowserOwnerRole),
  name: Schema.optional(TrimmedNonEmptyString),
  visibility: Schema.optionalKey(BrowserVisibility),
  viewport: Schema.optionalKey(BrowserViewport),
  initialUrl: Schema.optional(BrowserUrl),
});
export type BrowserOpenSessionInput = typeof BrowserOpenSessionInput.Type;

export const BrowserCloseSessionInput = Schema.Struct({
  threadId: ThreadId,
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
});
export type BrowserCloseSessionInput = typeof BrowserCloseSessionInput.Type;

export const BrowserGetSessionInput = Schema.Struct({
  threadId: ThreadId,
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
});
export type BrowserGetSessionInput = typeof BrowserGetSessionInput.Type;

export const BrowserListSessionsInput = Schema.Struct({
  threadId: Schema.optional(ThreadId),
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
});
export type BrowserListSessionsInput = typeof BrowserListSessionsInput.Type;

export const BrowserSubscribeInput = Schema.Struct({
  threadId: Schema.optional(ThreadId),
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
});
export type BrowserSubscribeInput = typeof BrowserSubscribeInput.Type;

export const BrowserClearBrowsingDataInput = Schema.Struct({
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
});
export type BrowserClearBrowsingDataInput = typeof BrowserClearBrowsingDataInput.Type;

export const BrowserClearBrowsingDataResult = Schema.Struct({
  closedSessionCount: Schema.Number,
  deletedSessionCount: Schema.Number,
  deletedCaptureCount: Schema.Number,
});
export type BrowserClearBrowsingDataResult = typeof BrowserClearBrowsingDataResult.Type;

export const BrowserCommandInput = Schema.Struct({
  threadId: ThreadId,
  browserId: Schema.optional(BrowserId),
  environmentId: Schema.optionalKey(EnvironmentId),
  userId: Schema.optionalKey(BrowserUserId),
  sessionEpoch: Schema.optional(BrowserSessionEpoch),
  controlSequence: Schema.optional(PositiveInt),
  command: BrowserCommand,
});
export type BrowserCommandInput = typeof BrowserCommandInput.Type;

const BrowserStreamEventBase = Schema.Struct({
  eventId: EventId,
  browserId: BrowserId,
  threadId: ThreadId,
  environmentId: EnvironmentId,
  userId: BrowserUserId,
  agentId: Schema.NullOr(BrowserAgentId),
  sessionEpoch: Schema.optional(BrowserSessionEpoch),
  createdAt: IsoDateTime,
});

const BrowserSessionStartedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("session.started"),
  payload: Schema.Struct({
    snapshot: BrowserSessionSnapshot,
  }),
});

const BrowserSessionUpdatedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("session.updated"),
  payload: Schema.Struct({
    snapshot: BrowserSessionSnapshot,
  }),
});

const BrowserSessionClosedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("session.closed"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    reason: Schema.optional(BrowserSessionCloseReason),
  }),
});

const BrowserDisplayUpdatedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("display.updated"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    display: BrowserDisplaySession,
  }),
});

const BrowserDisplayIceCandidateStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("display.iceCandidate"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
    clientId: BrowserDisplayClientId,
    candidate: Schema.NullOr(BrowserDisplayIceCandidate),
  }),
});

const BrowserDisplayMetricsStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("display.metrics"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
    clientId: BrowserDisplayClientId,
    metrics: BrowserDisplayMetrics,
  }),
});

const BrowserTabUpdatedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("tab.updated"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tab: BrowserTabSnapshot,
  }),
});

const BrowserTabClosedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("tab.closed"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
  }),
});

const BrowserSnapshotReadyStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("snapshot.ready"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
    captureId: BrowserCaptureId,
    capturedAt: IsoDateTime,
    url: BrowserUrl,
    viewport: BrowserViewport,
    content: Schema.String,
  }),
});

const BrowserScreenshotReadyStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("screenshot.ready"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
    captureId: BrowserCaptureId,
    capturedAt: IsoDateTime,
    url: BrowserUrl,
    viewport: BrowserViewport,
    imageDataUrl: Schema.String,
  }),
});

const BrowserFrameReadyStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("frame.ready"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
    streamId: Schema.optional(BrowserFrameStreamId),
    streamGeneration: Schema.optional(PositiveInt),
    sequence: Schema.Number,
    keyframe: Schema.optional(Schema.Boolean),
    format: Schema.Literal("jpeg"),
    imageDataUrl: Schema.String,
    capturedAt: IsoDateTime,
    url: BrowserUrl,
    viewport: BrowserViewport,
  }),
});

const BrowserCommandLatestCapture = Schema.Union([
  BrowserSnapshotReadyStreamEvent,
  BrowserScreenshotReadyStreamEvent,
]);

export const BrowserCommandResult = Schema.Struct({
  ok: Schema.Boolean,
  snapshot: BrowserSessionSnapshot,
  sessionEpoch: Schema.optional(BrowserSessionEpoch),
  acknowledgedControlSequence: Schema.optional(NonNegativeInt),
  detail: Schema.optional(TrimmedNonEmptyString),
  latestCapture: Schema.optional(BrowserCommandLatestCapture),
  latestFrame: Schema.optional(BrowserFrameReadyStreamEvent),
});
export type BrowserCommandResult = typeof BrowserCommandResult.Type;

const BrowserActionFailedStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("action.failed"),
  payload: Schema.Struct({
    commandType: TrimmedNonEmptyString,
    controlSequence: Schema.optional(PositiveInt),
    tabId: Schema.optional(BrowserTabId),
    message: TrimmedNonEmptyString,
  }),
});

const BrowserConsoleLogsReadyStreamEvent = Schema.Struct({
  ...BrowserStreamEventBase.fields,
  type: Schema.Literal("console.logs.ready"),
  payload: Schema.Struct({
    sessionId: BrowserSessionId,
    tabId: BrowserTabId,
    logs: Schema.Array(BrowserConsoleLogEntry),
  }),
});

export const BrowserStreamEvent = Schema.Union([
  BrowserSessionStartedStreamEvent,
  BrowserSessionUpdatedStreamEvent,
  BrowserSessionClosedStreamEvent,
  BrowserDisplayUpdatedStreamEvent,
  BrowserDisplayIceCandidateStreamEvent,
  BrowserDisplayMetricsStreamEvent,
  BrowserTabUpdatedStreamEvent,
  BrowserTabClosedStreamEvent,
  BrowserSnapshotReadyStreamEvent,
  BrowserScreenshotReadyStreamEvent,
  BrowserFrameReadyStreamEvent,
  BrowserActionFailedStreamEvent,
  BrowserConsoleLogsReadyStreamEvent,
]);
export type BrowserStreamEvent = typeof BrowserStreamEvent.Type;

// FORGE-216: `detail` was `TrimmedNonEmptyString`, which rejects strings with
// leading/trailing whitespace at construction time. Playwright errors arrive
// with multi-line `Call log:` blocks (trailing `\n`, indented lines), and the
// schema check threw an "Unhandled Error" outside the Effect fiber on every
// transient Chromium hiccup — converting recoverable retries into Flake
// Detection failures.
//
// `detail` is a server-side diagnostic string; full upstream fidelity matters
// more than trimmed cosmetics. Non-empty is still enforced so we don't
// construct a `BrowserError` with `detail: ""`.
// `message` mirrors `detail` because this error crosses the RPC boundary and
// is then read by the universal `error instanceof Error ? error.message : ...`
// pattern on the client. Without this getter, `message` is "" and every such
// consumer renders a BLANK error — the desktop Start pane shipped exactly that:
// a failed openSession looked like a click that did nothing.
export class BrowserError extends Schema.TaggedErrorClass<BrowserError>()("BrowserError", {
  detail: Schema.String.check(Schema.isNonEmpty()),
}) {
  override get message(): string {
    return this.detail;
  }
}
